import { Effect, Layer, Result, Schema, Scope } from "effect";
import { PanelBounds, panelPlacementFailure, type PanelPlacementFailure } from "./panel-placement-model.ts";
import { PanelPlacementIo } from "./panel-placement-service.ts";

const place = (mount: HTMLElement, destination: HTMLElement): Result.Result<void, PanelPlacementFailure> => {
  const read = Result.try(() => { const bounds = destination.getBoundingClientRect(); return { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height }; });
  if (Result.isFailure(read)) return Result.fail(panelPlacementFailure(read.failure));
  const decoded = Schema.decodeUnknownResult(PanelBounds)(read.success);
  if (Result.isFailure(decoded)) return Result.fail(panelPlacementFailure(decoded.failure));
  const bounds = decoded.success;
  const written = Result.try(() => {
    mount.style.position = "fixed";
    mount.style.left = `${bounds.left}px`;
    mount.style.top = `${bounds.top}px`;
    mount.style.width = `${bounds.width}px`;
    mount.style.height = `${bounds.height}px`;
    mount.style.zIndex = "1";
  });
  return Result.isFailure(written) ? Result.fail(panelPlacementFailure(written.failure)) : Result.succeed(undefined);
};
export const PanelPlacementIoLive = Layer.succeed(PanelPlacementIo, {
  find: (container, id) => Effect.try({ try: () => [...container.querySelectorAll<HTMLElement>("[id]")].find(node => node.id === id) ?? null, catch: panelPlacementFailure }),
  place: (mount, destination) => Effect.suspend(() => { const result = place(mount, destination); return Result.isFailure(result) ? Effect.fail(result.failure) : Effect.void; }),
  watch: (mount, container, destination, canRead, report) => Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const active = Effect.try({ try: () => owner.state._tag !== "Closed" && canRead(), catch: panelPlacementFailure });
    if (!(yield* active)) return;
    const update = () => {
      const allowed = Result.try(() => owner.state._tag !== "Closed" && canRead() && mount.isConnected && container.isConnected && container.contains(destination));
      if (Result.isFailure(allowed)) { report(panelPlacementFailure(allowed.failure)); return; }
      if (!allowed.success) return;
      const result = place(mount, destination);
      if (Result.isFailure(result)) report(result.failure);
    };
    const observer = yield* Effect.try({ try: () => new ResizeObserver(update), catch: panelPlacementFailure });
    yield* Effect.addFinalizer(() => Effect.try({ try: () => observer.disconnect(), catch: panelPlacementFailure }).pipe(Effect.orDie));
    if (!(yield* active)) return;
    yield* Effect.try({ try: () => observer.observe(destination), catch: panelPlacementFailure });
    yield* Effect.addFinalizer(() => Effect.try({ try: () => window.removeEventListener("resize", update), catch: panelPlacementFailure }).pipe(Effect.orDie));
    if (!(yield* active)) return;
    yield* Effect.try({ try: () => window.addEventListener("resize", update), catch: panelPlacementFailure });
    yield* Effect.addFinalizer(() => Effect.try({ try: () => window.removeEventListener("scroll", update, true), catch: panelPlacementFailure }).pipe(Effect.orDie));
    if (!(yield* active)) return;
    yield* Effect.try({ try: () => window.addEventListener("scroll", update, true), catch: panelPlacementFailure });
  })
});
